import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import UserInfo from "../../../components/UserInfo";
import SabhaSaarHeader from "../../../components/SabhaSaarHeader";
import TopHeader from "../../../components/TopHeader";
import Wrapper from "../../../assets/wrappers/PreviousMeetingSingle";
import { toast } from "react-toastify";
import { marked } from "marked";
import MeetingSummary from "./MeetingSummary";
import PanchayatiRajLogo from '../../../assets/images/PanchayatiRajLogo.png';
import html2pdf from "html2pdf.js";
import Footer from "../../../components/Footer";
import protectedAPI from "../../../services/axiosInstance";
import { disclaimerTranslations, en_translate_language_map } from "../../../constants/disclaimerTranslations";
import { stopGlobalAudio } from "../../../utils/audioManager";

const IoArrowBack = require("react-icons/io5").IoArrowBack;

const PreviousMeetingSingle: React.FC = () => {
    const baseUrl = process.env.REACT_APP_BASE_URL;
    const navigate = useNavigate();
    const { taskId } = useParams<{ taskId: string }>();
    const [data, setData] = useState<any | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [summaryHtml, setSummaryHtml] = useState({
        englishSummary: "",
        regionalSummary: ""
    });
    const [openIndex, setOpenIndex] = useState<number | null>(null);

    const [storageData, setStorageData] = useState<any>(() => {
        const saved = localStorage.getItem('loginDetails');
        try {
            return saved ? JSON.parse(saved) : {};
        } catch (e) {
            console.error('Failed to parse loginDetails from localStorage:', e);
            return {};
        }
    });

    useEffect(() => {
        if (!taskId) return;

        const fetchData = async () => {
            try {
                setLoading(true);
                const response = await protectedAPI.get(
                    `${baseUrl}api/meeting-details/${taskId}`
                );
                setData(response.data);
            } catch (err: any) {
                setError("Failed to fetch meeting details");
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [taskId]);

    const normalizeMarkdown = (text: string) => {
        return text.replace(/\*\*\s+</g, "**<").replace(/>\s+\*\*/g, ">**");
    };

    useEffect(() => {
        const convertSummary = async () => {
            if (data?.english_minutes || data?.translated_minutes) {

                const regionalHtml = data?.translated_minutes
                    ? await marked.parse(normalizeMarkdown(data.translated_minutes))
                    : "";

                const englishHtml = data?.english_minutes
                    ? await marked.parse(normalizeMarkdown(data.english_minutes))
                    : "";


                setSummaryHtml({
                    englishSummary: englishHtml,
                    regionalSummary: regionalHtml,
                });
            }
        };
        convertSummary();
    }, [data?.english_minutes, data?.translated_minutes]);

const handleExportPDF = (
    htmlContent: string,
    lang: string = "english",
    fileName: string = "Minutes_of_Meeting.pdf"
) => {
    if (!htmlContent) {
        toast.error("No content to export.");
        return;
    }

    const {
        state_name_english,
        zpname,
        zpcode,
        bpname,
        bpcode,
        gpname,
        gpcode,
    } = storageData || {};

    const meetingType = data?.meeting_type || "-";
    const meetingTitle = data?.meeting_title || "-";

    let langCode = "en";

    if (lang && lang.toLowerCase() !== "english") {
        const foundLang = Object.entries(en_translate_language_map).find(
            ([, name]) => name.toLowerCase() === lang.toLowerCase()
        );
        langCode = foundLang ? foundLang[0] : "en";
    }

    const englishDisclaimer = disclaimerTranslations["en"];
    const regionalDisclaimer = disclaimerTranslations[langCode];

    let disclaimerHTML = "";

    if (langCode === "en") {
        disclaimerHTML = `
        <div class="pdf-block" style="margin-top:10px">
            <p><b class="text-danger">${englishDisclaimer.title}:</b> ${englishDisclaimer.text}</p>
        </div>`;
    } else {
        disclaimerHTML = `
        <div class="pdf-block" style="margin-top:10px">
            <p><b class="text-danger">${regionalDisclaimer?.title}:</b> ${regionalDisclaimer?.text}</p>
            <p><b class="text-danger">${englishDisclaimer.title}:</b> ${englishDisclaimer.text}</p>
        </div>`;
    }

    const element = document.createElement("div");
    element.style.width = "100%";
    element.style.maxWidth = "794px";

    element.innerHTML = `

<link href="https://fonts.googleapis.com/css2?family=Noto+Sans:wght@400;500&display=swap" rel="stylesheet">

<style>

.pdf-root{
  font-family:
    'Noto Sans',
    'Noto Sans Devanagari',
    'Noto Sans Ol Chiki',
    Arial,
    sans-serif;
  line-height:1.7;
  font-size : 19px;
}

.pdf-root p,
.pdf-root li{
  line-height:1.7;
  word-break:normal;
  overflow-wrap:anywhere;
  white-space:normal;
  margin-bottom:6px;
  page-break-inside:avoid;
  break-inside:avoid;
}

.pdf-root ul,
.pdf-root ol{
  padding-left:18px;
}

.pdf-root h1,
.pdf-root h2,
.pdf-root h3,
.pdf-root h4{
  page-break-after:avoid;
}

.pdf-root img{
  max-width:100%;
  page-break-inside:avoid;
}

.pdf-block{
  page-break-inside:avoid;
  break-inside:avoid;
}

.minutes-content{
  line-height:1.7;
}

.disclaimer{
  margin-top: 8px;
}

</style>

<div class="pdf-root">

<!-- Header -->
<div class="pdf-block" style="display:flex; align-items:center; gap:7px; margin-bottom:10px;">
    <img src="${PanchayatiRajLogo}" alt="Logo" style="height:50px;" />
    ${state_name_english ? `<div>State: ${state_name_english}</div>` : ""}
    ${zpname ? `<div>ZP: ${zpname}${zpcode ? ` (${zpcode})` : ""}</div>` : ""}
    ${bpname ? `<div>BP: ${bpname}${bpcode ? ` (${bpcode})` : ""}</div>` : ""}
    ${gpname ? `<div>GP: ${gpname}${gpcode ? ` (${gpcode})` : ""}</div>` : ""}
</div>

<hr/>

<!-- Meeting Info -->
<div class="pdf-block">
    <div><b>Meeting Type:</b> ${meetingType}</div>
    <div><b>Meeting Title:</b> ${meetingTitle}</div>
</div>

<!-- Minutes -->
<div class="minutes-content">
    ${htmlContent}
</div>

<!-- Disclaimer -->
${disclaimerHTML}

</div>
`;

    const opt: any = {
        margin: [15, 10, 15, 10],
        filename: fileName,
        image: { type: "jpeg", quality: 0.98 },

        html2canvas: {
            scale: 2,
            useCORS: true,
            letterRendering: true
        },

        jsPDF: {
            unit: "mm",
            format: "a4",
            orientation: "portrait"
        },

        pagebreak: {
            mode: ["css", "legacy"]
        }
    };

    html2pdf()
        .set(opt)
        .from(element)
        .save()
        .then(() => toast.success("Exported as PDF"))
        .catch((err: any) => {
            console.error(err);
            toast.error("Failed to export PDF.");
        });
};




    return (
        <Wrapper>
            <TopHeader />
            <section className="body-content">
                <SabhaSaarHeader />
                <UserInfo />
                <div className="container" id="uploadMainSkip">
                    <div className="card mt-3">
                        <div className="card-body">
                            <div className="d-flex justify-content-between">
                                <h5 className="card-title-cus">
                                    View Previous Meetings Details
                                </h5>
                                <div
                                    className="goBack"
                                    onClick={() => {
                                         stopGlobalAudio();
                                       navigate("/PreviousMeetingsDetails") 
                                    }}
                                >
                                    <IoArrowBack size={24} /> Back
                                </div>
                            </div>
                            <div className="">
                                {data?.meeting_type && <p className="mb-0"> <b>Metting Type : </b>{data.meeting_type}</p>}
                                {data?.meeting_title && <p> <b>Metting Title :</b> {data.meeting_title}</p>}
                            </div>
                            {loading && <p className="text-center">Loading...</p>}
                            {error && <p className="text-danger">Error Occured !!!</p>}
                            {data?.media_type && data?.video_url && (
                                <div className="preview-area mt-3">
                                    {data.media_type === "video" ? (
                                        <video width="100%" height="auto" controls>
                                            <source src={data.video_url} type="video/mp4" />
                                            Your browser does not support the video tag.
                                        </video>
                                    ) : data.media_type === "audio" ? (
                                        <audio controls>
                                            <source src={data.video_url} type="audio/mpeg" />
                                            Your browser does not support the audio tag.
                                        </audio>
                                    ) : null}
                                </div>
                            )}

                        </div>
                        <div className="card">
                            <div className="card-body">
                                <h4 className="text-center">Final Results Section</h4>
                                {summaryHtml.englishSummary && (
                                    <MeetingSummary
                                        task_id = {taskId}
                                        title="View Minutes of the Meeting (English)"
                                        htmlContent={summaryHtml.englishSummary}
                                        rawContent={data?.english_minutes || ""}
                                        onExportPDF={() => handleExportPDF(summaryHtml.englishSummary, 'english', "Minutes_English.pdf")}
                                        isOpen={openIndex === 0}
                                        onToggle={() => setOpenIndex(openIndex === 0 ? null : 0)}
                                    />
                                )}

                                {summaryHtml.regionalSummary && (
                                    <MeetingSummary
                                        task_id = {taskId}
                                        title={`View Minutes of the Meeting (${data.translated_minutes_lang})`}
                                        htmlContent={summaryHtml.regionalSummary}
                                        rawContent={data?.translated_minutes || ""}
                                        onExportPDF={() => handleExportPDF(summaryHtml.regionalSummary, data.translated_minutes_lang, `Minutes_${data.translated_minutes_lang}.pdf`)}
                                        isOpen={openIndex === 1}
                                        onToggle={() => setOpenIndex(openIndex === 1 ? null : 1)}
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="card bg-danger-cus mt-2">
                        <div className="card-body">
                            <h5 className="card-heading text-center text-danger">DISCLAIMER</h5>
                            <p>
                                The AI-generated summaries are based on uploaded audio/video and may contain errors due to input quality or language variation. Please verify the content before official use. The Ministry of Panchayati Raj is not liable for any discrepancies.
                            </p>
                        </div>
                    </div>
                </div>
            </section>
            <Footer />
        </Wrapper>
    );
};

export default PreviousMeetingSingle;
